import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Link } from "react-router-dom";
import { Users, Package, Gift, Inbox, Check, X, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState({ products: 0, rewards: 0, clients: 0, pending: 0, unreadMessages: 0 });
  const [redemptions, setRedemptions] = useState([]);

  const load = async () => {
    const [p, r, c, red, msg] = await Promise.all([
      api.get("/products"),
      api.get("/rewards"),
      api.get("/admin/clients"),
      api.get("/admin/redemptions"),
      api.get("/admin/messages"),
    ]);
    setStats({
      products: p.data.length,
      rewards: r.data.length,
      clients: c.data.length,
      pending: red.data.filter((x)=>x.status==="pending").length,
      unreadMessages: msg.data.filter((x)=>!x.read).length,
    });
    setRedemptions(red.data);
  };

  useEffect(() => { load(); }, []);

  const decide = async (id, status) => {
    try {
      await api.put(`/admin/redemptions/${id}`, { status });
      toast.success(status === "approved" ? "Demande approuvée" : "Demande refusée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="admin-dashboard">
      <div className="fade-up">
        <div className="label-eyebrow mb-3">Espace administrateur</div>
        <h1 className="font-display text-5xl mb-2">Pilotage de la maison</h1>
        <p className="text-[#a3a8a4]">Vue d'ensemble en temps réel.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[rgba(253,251,247,0.08)] border border-[rgba(253,251,247,0.08)] mt-10 fade-up-delay-1">
        <StatCell icon={<Package size={18}/>} label="Produits" value={stats.products} link="/admin/produits"/>
        <StatCell icon={<Gift size={18}/>} label="Récompenses" value={stats.rewards} link="/admin/recompenses"/>
        <StatCell icon={<Users size={18}/>} label="Clients" value={stats.clients} link="/admin/clients"/>
        <StatCell icon={<Inbox size={18}/>} label="En attente" value={stats.pending}/>
        <StatCell icon={<MessageSquare size={18}/>} label="Messages" value={stats.unreadMessages} link="/admin/messages"/>
      </div>

      <div className="mt-16 fade-up-delay-2">
        <div className="label-eyebrow mb-2">Demandes de récompense</div>
        <h2 className="font-display text-3xl mb-6">À traiter</h2>
        <div className="border border-[rgba(253,251,247,0.08)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[rgba(253,251,247,0.08)] text-left">
                <th className="px-4 py-3 label-eyebrow">Client</th>
                <th className="px-4 py-3 label-eyebrow">Récompense</th>
                <th className="px-4 py-3 label-eyebrow">Points</th>
                <th className="px-4 py-3 label-eyebrow">Date</th>
                <th className="px-4 py-3 label-eyebrow">Statut</th>
                <th className="px-4 py-3 label-eyebrow text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {redemptions.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-[#a3a8a4]">Aucune demande pour le moment.</td></tr>
              )}
              {redemptions.map((r) => (
                <tr key={r.id} className="border-b border-[rgba(253,251,247,0.05)] last:border-0 hover:bg-[#1a241b]/30" data-testid={`admin-redemption-${r.id}`}>
                  <td className="px-4 py-3">{r.user_firstName} <span className="text-[#a3a8a4]">· {r.user_email}</span></td>
                  <td className="px-4 py-3">{r.reward_name}</td>
                  <td className="px-4 py-3 gold-text">{r.points_cost}</td>
                  <td className="px-4 py-3 text-[#a3a8a4]">{new Date(r.created_at).toLocaleString("fr-FR")}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.status}/></td>
                  <td className="px-4 py-3 text-right">
                    {r.status === "pending" ? (
                      <div className="inline-flex gap-2">
                        <button onClick={() => decide(r.id, "approved")} className="px-3 py-1.5 border border-emerald-500 text-emerald-400 hover:bg-emerald-500/10 inline-flex items-center gap-1" data-testid={`approve-${r.id}`}><Check size={14}/> Approuver</button>
                        <button onClick={() => decide(r.id, "rejected")} className="px-3 py-1.5 border border-red-500 text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1" data-testid={`reject-${r.id}`}><X size={14}/> Refuser</button>
                      </div>
                    ) : <span className="text-[#a3a8a4] text-xs">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCell({ icon, label, value, link }) {
  const inner = (
    <div className="bg-[#0a0e0b] p-8 hover:bg-[#0d130e] transition-colors h-full">
      <div className="flex items-center gap-2 label-eyebrow mb-3">{icon} {label}</div>
      <div className="font-display text-5xl gold-text">{value}</div>
    </div>
  );
  return link ? <Link to={link} data-testid={`stat-${label}`}>{inner}</Link> : inner;
}

function StatusBadge({ status }) {
  const map = { pending: "En attente", approved: "Approuvée", rejected: "Refusée" };
  const color = { pending: "text-[#d4af37]", approved: "text-emerald-400", rejected: "text-red-400" }[status];
  return <span className={`text-xs uppercase tracking-widest ${color}`}>{map[status] || status}</span>;
}
